/**
 * useProjectAssets - Focused hook for DB-backed media-library project assets.
 *
 * Extracted from `useMediaLibrary` so multiple consumers (the picker popup and
 * the downloaded-assets section) can share the same data + cache without
 * dragging along the provider-search machinery. The module-level cache keyed
 * by `projectId` means data loaded in one consumer is instantly available to
 * the other — opening the Media Library panel after using the picker popup
 * (or vice versa) shows populated state with no spinner.
 *
 * Exposes:
 *   - Data: `projectAssets`, `totalProjectAssets`, `isLoadingAssets`.
 *   - Pagination: `assetsHasMore`, `isLoadingMoreAssets`, `loadMoreAssets`.
 *   - Loaders: `loadProjectAssets` (cache-aware no-op when fresh),
 *              `refreshProjectAssets` (always refetches, used after
 *              downloads/deletes).
 *   - Selection: `selectedAssets`, `toggleAssetSelection`,
 *                `clearAssetSelection`, `selectAllAssets`.
 *   - Deletion: `isDeleting`, `handleDeleteSelected`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import { auth } from "@/lib/firebase";
import type { PageInfo } from "@/types/pagination.types";
import type {
  MediaLibraryAsset,
  MediaType,
} from "@/types/media-library-panel.types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const ASSETS_PER_PAGE = 30;
const PROJECT_ASSETS_CACHE_TTL_MS = 5 * 60 * 1000;

export interface ProjectAssetsCountByType {
  image: number;
  video: number;
  audio: number;
}

interface ProjectAssetsCacheEntry {
  assets: MediaLibraryAsset[];
  total: number;
  /** Server-side totals per media type — accurate even when only one
   *  page is loaded. UI tab pills should read from here, not from
   *  `assets.filter(a => a.type === t).length`. */
  countByType: ProjectAssetsCountByType;
  hasMore: boolean;
  currentPage: number;
  timestamp: number;
}

const EMPTY_COUNT_BY_TYPE: ProjectAssetsCountByType = {
  image: 0,
  video: 0,
  audio: 0,
};

// Module-level — survives hook unmount/remount so switching between the
// picker popup and the Media Library panel never re-hits the network as long
// as the TTL hasn't lapsed.
const _projectAssetsCache = new Map<string, ProjectAssetsCacheEntry>();

function _readProjectAssetsCache(
  projectId: string | undefined,
): ProjectAssetsCacheEntry | null {
  if (!projectId) return null;
  const entry = _projectAssetsCache.get(projectId);
  if (!entry) return null;
  if (Date.now() - entry.timestamp >= PROJECT_ASSETS_CACHE_TTL_MS) return null;
  return entry;
}

function _inferType(mime: string, tags: string[] = []): MediaType {
  if (tags.includes("image") || mime.startsWith("image/")) return "image";
  if (tags.includes("video") || mime.startsWith("video/")) return "video";
  return "audio";
}

interface RawProjectAssetRow {
  fileRefId: string;
  filename: string;
  publicUrl: string | null;
  mimeType: string;
  fileSize: number;
  createdAt: string;
  tags?: string[];
}

function _mapProjectAssetsToLocal(
  items: RawProjectAssetRow[],
): MediaLibraryAsset[] {
  return items
    .filter((it) => !!it.publicUrl)
    .map((it) => ({
      id: `r2-${it.fileRefId}`,
      name: it.filename,
      path: it.publicUrl!,
      assetPath: it.publicUrl!,
      size: it.fileSize,
      mimeType: it.mimeType,
      type: _inferType(it.mimeType, it.tags ?? []),
      createdAt: it.createdAt,
      modifiedAt: it.createdAt,
      previewUrl: it.publicUrl!,
      fileRefId: it.fileRefId,
    }));
}

export interface UseProjectAssetsReturn {
  projectAssets: MediaLibraryAsset[];
  totalProjectAssets: number;
  /** Server-side per-type totals. Populated by the `/project-assets`
   *  endpoint's `countByType` field — safe to use for UI tab counts
   *  even while pagination is partial. */
  projectAssetsCountByType: ProjectAssetsCountByType;
  isLoadingAssets: boolean;

  /** Cache-aware — no-op when the module cache is still fresh. */
  loadProjectAssets: () => Promise<void>;
  /** Bypasses cache; forces a network refetch. Use after download/delete. */
  refreshProjectAssets: () => Promise<void>;

  assetsHasMore: boolean;
  isLoadingMoreAssets: boolean;
  loadMoreAssets: () => Promise<void>;

  selectedAssets: Set<string>;
  toggleAssetSelection: (id: string) => void;
  clearAssetSelection: () => void;
  selectAllAssets: () => void;

  isDeleting: boolean;
  handleDeleteSelected: () => Promise<void>;
}

export function useProjectAssets(
  projectId: string | undefined,
): UseProjectAssetsReturn {
  // Lazy initializers hydrate from the module cache so remounting (e.g.
  // closing the media panel and reopening, or mounting the picker popup
  // after the panel already loaded) shows the previously-loaded data with
  // zero flash.
  const [projectAssets, setProjectAssets] = useState<MediaLibraryAsset[]>(
    () => _readProjectAssetsCache(projectId)?.assets ?? [],
  );
  const [totalProjectAssets, setTotalProjectAssets] = useState(
    () => _readProjectAssetsCache(projectId)?.total ?? 0,
  );
  const [projectAssetsCountByType, setProjectAssetsCountByType] =
    useState<ProjectAssetsCountByType>(
      () =>
        _readProjectAssetsCache(projectId)?.countByType ?? EMPTY_COUNT_BY_TYPE,
    );
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [assetsPage, setAssetsPage] = useState(
    () => _readProjectAssetsCache(projectId)?.currentPage ?? 1,
  );
  const [assetsHasMore, setAssetsHasMore] = useState(
    () => _readProjectAssetsCache(projectId)?.hasMore ?? false,
  );
  const [isLoadingMoreAssets, setIsLoadingMoreAssets] = useState(false);

  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch a single page. `append=false` replaces the list (page 1 / refresh);
  // `append=true` dedupes and appends (infinite scroll).
  const _fetchProjectAssetsPage = useCallback(
    async (page: number, append: boolean): Promise<void> => {
      if (!projectId) return;

      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/media-library/project-assets`,
        {
          params: { projectId, page, perPage: ASSETS_PER_PAGE },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.data.success || !Array.isArray(response.data.items)) {
        if (!append) {
          setProjectAssets([]);
          setTotalProjectAssets(0);
        }
        setAssetsHasMore(false);
        return;
      }

      const newAssets = _mapProjectAssetsToLocal(response.data.items);
      const meta = response.data.meta as PageInfo | undefined;
      const nextHasMore = meta
        ? meta.currentPage < meta.totalPages
        : newAssets.length >= ASSETS_PER_PAGE;

      setAssetsPage(page);
      setAssetsHasMore(nextHasMore);
      if (meta) setTotalProjectAssets(meta.total);

      // Per-type totals come from the server on every page load. Safe to
      // overwrite on append since the endpoint returns the same aggregate
      // regardless of pagination.
      const serverCountByType = response.data.countByType as
        | ProjectAssetsCountByType
        | undefined;
      if (serverCountByType) {
        setProjectAssetsCountByType({
          image: Number(serverCountByType.image) || 0,
          video: Number(serverCountByType.video) || 0,
          audio: Number(serverCountByType.audio) || 0,
        });
      }

      if (append) {
        setProjectAssets((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          const unique = newAssets.filter((a) => !seen.has(a.id));
          return unique.length ? [...prev, ...unique] : prev;
        });
      } else {
        setProjectAssets(newAssets);
        if (!meta) setTotalProjectAssets(newAssets.length);
      }
    },
    [projectId],
  );

  // Cache-aware initial load: no-op when the cache is still fresh (state was
  // already seeded via the lazy useState initializers above).
  const loadProjectAssets = useCallback(async () => {
    if (!projectId) return;
    if (_readProjectAssetsCache(projectId)) return;

    setIsLoadingAssets(true);
    try {
      await _fetchProjectAssetsPage(1, false);
    } catch (error) {
      console.error("Failed to load media assets:", error);
      setProjectAssets([]);
      setTotalProjectAssets(0);
      setAssetsHasMore(false);
    } finally {
      setIsLoadingAssets(false);
    }
  }, [projectId, _fetchProjectAssetsPage]);

  // Cache-bypassing reload. Used after mutations (download completed, asset
  // deleted) where we want the authoritative server view, not the cache.
  const refreshProjectAssets = useCallback(async () => {
    if (!projectId) return;
    _projectAssetsCache.delete(projectId);
    setIsLoadingAssets(true);
    try {
      await _fetchProjectAssetsPage(1, false);
    } catch (error) {
      console.error("Failed to refresh media assets:", error);
    } finally {
      setIsLoadingAssets(false);
    }
  }, [projectId, _fetchProjectAssetsPage]);

  // Write-through cache: mirror every committed state change into the module
  // cache. Skipped while an in-flight load is populating state — otherwise
  // the transient `[]` between reset and fetch-completion would overwrite
  // good cached data.
  useEffect(() => {
    if (!projectId) return;
    if (isLoadingAssets) return;
    if (projectAssets.length === 0) return;
    _projectAssetsCache.set(projectId, {
      assets: projectAssets,
      total: totalProjectAssets,
      countByType: projectAssetsCountByType,
      hasMore: assetsHasMore,
      currentPage: assetsPage,
      timestamp: Date.now(),
    });
  }, [
    projectId,
    projectAssets,
    totalProjectAssets,
    projectAssetsCountByType,
    assetsHasMore,
    assetsPage,
    isLoadingAssets,
  ]);

  const loadMoreAssets = useCallback(async () => {
    if (!projectId) return;
    if (!assetsHasMore || isLoadingMoreAssets) return;
    setIsLoadingMoreAssets(true);
    try {
      await _fetchProjectAssetsPage(assetsPage + 1, true);
    } catch (error) {
      console.error("Failed to load more media assets:", error);
    } finally {
      setIsLoadingMoreAssets(false);
    }
  }, [
    projectId,
    _fetchProjectAssetsPage,
    assetsHasMore,
    isLoadingMoreAssets,
    assetsPage,
  ]);

  const toggleAssetSelection = useCallback((id: string) => {
    setSelectedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearAssetSelection = useCallback(() => {
    setSelectedAssets(new Set());
  }, []);

  const selectAllAssets = useCallback(() => {
    setSelectedAssets(new Set(projectAssets.map((a) => a.id)));
  }, [projectAssets]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedAssets.size === 0) return;

    const token = await auth.getIdToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setIsDeleting(true);
    try {
      // DB-backed delete (post-R2 migration): one DELETE per fileRefId; each
      // call removes the R2 object and the files/file_links rows.
      const toDelete = projectAssets
        .filter((a) => selectedAssets.has(a.id) && a.fileRefId)
        .map((a) => a.fileRefId!);

      const results = await Promise.allSettled(
        toDelete.map((refId) =>
          axios.delete(`${API_URL}/api/media-library/project-assets/${refId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ),
      );

      const totalDeleted = results.filter(
        (r) => r.status === "fulfilled" && r.value.data?.success,
      ).length;
      const totalFailed = results.length - totalDeleted;

      if (totalDeleted > 0) {
        toast.success(
          `Deleted ${totalDeleted} item${totalDeleted > 1 ? "s" : ""}`,
        );
        // Optimistic removal so the UI reflects the change instantly.
        // Also decrement per-type counts keyed off the deleted rows so the
        // tab pills update before `refreshProjectAssets` replies.
        const deletedTypeDelta = { image: 0, video: 0, audio: 0 };
        for (const a of projectAssets) {
          if (selectedAssets.has(a.id)) deletedTypeDelta[a.type] += 1;
        }
        setProjectAssets((prev) =>
          prev.filter((a) => !selectedAssets.has(a.id)),
        );
        setTotalProjectAssets((prev) => Math.max(0, prev - totalDeleted));
        setProjectAssetsCountByType((prev) => ({
          image: Math.max(0, prev.image - deletedTypeDelta.image),
          video: Math.max(0, prev.video - deletedTypeDelta.video),
          audio: Math.max(0, prev.audio - deletedTypeDelta.audio),
        }));
        setSelectedAssets(new Set());
        // Authoritative refetch — reconciles drift (concurrent uploads,
        // partial failures already counted as success, etc.).
        void refreshProjectAssets();
      }

      if (totalFailed > 0) {
        toast.error(`Failed to delete ${totalFailed} item(s)`);
      }
    } catch (error: unknown) {
      console.error("Delete failed:", error);
      let errorMessage = "Delete failed";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedAssets, projectAssets, refreshProjectAssets]);

  return {
    projectAssets,
    totalProjectAssets,
    projectAssetsCountByType,
    isLoadingAssets,
    loadProjectAssets,
    refreshProjectAssets,
    assetsHasMore,
    isLoadingMoreAssets,
    loadMoreAssets,
    selectedAssets,
    toggleAssetSelection,
    clearAssetSelection,
    selectAllAssets,
    isDeleting,
    handleDeleteSelected,
  };
}
